import { completeCcOrder, failCcOrder, createWooCommerceOrder } from './orderUtils';
import { getLineChargeTotal } from './cartPricing';
import { get, post } from './api';
import { getPersistentKioskId } from './kioskId';

// Add missing import
const { CardElement } = require('@stripe/react-stripe-js');

/**
 * Create a payment intent
 * @param {number} amount - Amount in cents
 * @param {string|null} orderId - Optional WooCommerce order ID (if already created)
 * @param {Object} options - Additional options
 * @param {string} options.customerId - Customer ID
 * @param {string} options.customerEmail - Customer email
 * @param {number} options.itemsCount - Number of items in cart
 * @param {Object} options.metadata - Additional metadata
 * @returns {Promise<Object>} Payment intent response
 */
export const createPaymentIntent = async (amount, orderId = null, options = {}) => {
  try {
    const kioskId = options.metadata?.kiosk_id || getPersistentKioskId();
    const payload = {
      amount,
      currency: 'usd',
      metadata: {
        order_id: orderId || '',
        customer_id: options.customerId || 'guest',
        customer_email: options.customerEmail || '',
        items_count: options.itemsCount || 0,
        source: options.metadata?.source || 'kiosk_checkout',
        ...(kioskId ? { kiosk_id: kioskId } : {})
      },
      description: `Payment for ${options.itemsCount || 0} items`,
      receipt_email: options.customerEmail,
      setup_future_usage: 'off_session',
      confirm: false
    };

    console.log('🔵 [DEBUG] Creating payment intent:', JSON.stringify({
      endpoint: '/api/payments/create-payment-intent',
      ...payload
    }, null, 2));

    const response = await post('/api/payments/create-payment-intent', payload);
    
    if (!response.clientSecret) {
      throw new Error('No client secret received from payment intent');
    }

    return response;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error(error.message || 'Failed to initialize payment');
  }
};

// ================= Stripe Terminal (server-driven) helpers =================

/**
 * Create a Terminal PaymentIntent via backend (amount in dollars; backend will convert to cents)
 */
export const createTerminalPaymentIntent = async ({
  amount,
  currency = 'usd',
  description,
  metadata = {},
  receipt_email,
  billing_first_name,
  billing_last_name
}) => {
  // Send amount in minor units (cents) as required by Stripe
  const amountInCents = Math.round(Number(amount || 0) * 100);
  console.log('[Terminal][FE] createTerminalPaymentIntent payload:', {
    inputAmount: amount,
    computedAmountInCents: amountInCents,
    currency,
    description,
    metaKeys: Object.keys(metadata || {}),
    receipt_email: receipt_email || undefined
  });
  const resp = await post('/api/stripe/terminal/payment-intent', {
    amount: amountInCents,
    currency,
    description,
    metadata,
    receipt_email,
    billing_first_name,
    billing_last_name
  });
  console.log('[Terminal][FE] createTerminalPaymentIntent response:', resp);
  return resp;
};

/**
 * Ask backend to process the PaymentIntent on the configured reader
 */
export const processPaymentIntentOnReader = async ({ payment_intent_id, reader_id, kiosk_id }) => {
  return post('/api/stripe/terminal/readers/process-payment-intent', { 
    payment_intent_id, 
    reader_id,
    kiosk_id // Pass kiosk_id to backend
  });
};

/**
 * Poll/fetch PaymentIntent status
 */
export const getPaymentIntentStatus = async (paymentIntentId) => {
  try {
    if (!paymentIntentId) {
      return { status: 'error', error: 'Missing paymentIntentId' };
    }
    const idStr = String(paymentIntentId);
    if (!idStr.startsWith('pi_')) {
      // Likely a PaymentMethod (pm_) or something else was passed by mistake
      return { status: 'invalid_id', id: idStr, error: `Expected PaymentIntent id (pi_*), received: ${idStr}` };
    }

    const resp = await get(`/api/stripe/terminal/payment-intent/${idStr}`);
    // If backend returns an error shape instead of a PI
    if (resp && resp.error) {
      return { status: 'error', id: idStr, error: resp.error };
    }
    return resp;
  } catch (e) {
    return { status: 'error', id: paymentIntentId, error: e?.message || 'Failed to fetch PaymentIntent' };
  }
};

/**
 * Capture a Terminal PaymentIntent
 */
export const capturePaymentIntent = async (paymentIntentId) => {
  return post(`/api/stripe/terminal/capture/${paymentIntentId}`);
};

/**
 * Cancel a Terminal PaymentIntent
 */
export const cancelPaymentIntent = async (paymentIntentId) => {
  return post(`/api/stripe/terminal/payment-intent/${paymentIntentId}/cancel`);
};

/**
 * Clear/cancel the terminal reader action (only if reader has an active action)
 * This stops the terminal from waiting for a card swipe, but only if needed
 * @param {number|null} kioskId - Kiosk ID to identify which terminal to clear
 * @param {boolean} force - If true, attempt to cancel even if reader appears idle (default: false)
 */
export const clearTerminal = async (kioskId = null, force = false) => {
  try {
    // Get kioskId from localStorage if not provided
    let effectiveKioskId = kioskId;
    if (!effectiveKioskId && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('kiosk_kioskId');
        if (stored) {
          effectiveKioskId = parseInt(stored, 10);
        }
      } catch (e) {
        console.warn('[Clear Terminal] Failed to read kioskId from localStorage:', e);
      }
    }
    
    if (!effectiveKioskId) {
      console.warn('[Clear Terminal] No kioskId available, skipping terminal clear');
      return null;
    }
    
    console.log('[Clear Terminal] Checking terminal status for kiosk:', effectiveKioskId, force ? '(force cancel)' : '(smart cancel)');
    const result = await post('/api/stripe/terminal/readers/cancel-action', {
      kiosk_id: effectiveKioskId,
      force: force
    });
    
    // If reader was already idle, log it but don't treat as error
    if (result?.status === 'idle' || result?.message?.includes('already idle')) {
      console.log('[Clear Terminal] Terminal is already idle, no action needed');
      return result;
    }
    
    // If reader is busy, log it but don't treat as error (it will become idle automatically)
    if (result?.status === 'busy' || result?.error_code === 'terminal_reader_busy') {
      console.log('[Clear Terminal] Terminal is busy, will become idle automatically');
      return result;
    }
    
    return result;
  } catch (error) {
    // Handle terminal_reader_busy error gracefully
    if (error?.code === 'terminal_reader_busy' || error?.message?.includes('busy')) {
      console.log('[Clear Terminal] Terminal is busy, will become idle automatically');
      return { status: 'busy', message: 'Reader is busy, will become idle automatically' };
    }
    console.warn('[Clear Terminal] Failed to clear terminal (non-fatal):', error?.message);
    return null;
  }
};

/**
 * Update PaymentIntent metadata/description (attach WooCommerce order details)
 */
export const updatePaymentIntentMetadata = async (paymentIntentId, { metadata = {}, description } = {}) => {
  if (!paymentIntentId || !String(paymentIntentId).startsWith('pi_')) {
    throw new Error('Invalid PaymentIntent id');
  }
  const body = {};
  if (metadata && typeof metadata === 'object') body.metadata = metadata;
  if (typeof description === 'string') body.description = description;
  if (!body.metadata && !body.description) return null;
  return post(`/api/stripe/terminal/payment-intent/${paymentIntentId}/metadata`, body);
};

/**
 * Process a credit card payment through Stripe
 * @param {Object} params
 * @param {Object} params.stripe - Stripe instance
 * @param {Object} params.elements - Stripe Elements instance
 * @param {number} params.amount - Payment amount in cents
 * @param {Object} params.customer - Customer information
 * @param {Object} params.order - The WooCommerce order object
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const processStripePayment = async ({
  stripe,
  elements,
  amount,
  customer,
  order,
  onSuccess,
  onError,
  getSession = null,
}) => {
  try {
    if (!stripe || !elements) {
      throw new Error('Stripe has not been initialized');
    }

    const customerName = customer?.name || `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
    // Use the order total directly as it already includes the credit card fee
    // The backend will handle the conversion to cents
    const orderTotal = parseFloat(order.total || amount || 0);
    
    // 1. Create payment intent with all necessary details
    const paymentIntentPayload = {
      amount: orderTotal,
      currency: order.currency || 'usd',
      metadata: { 
        order_id: order.id,
        order_key: order.order_key || '',
        customer_id: customer?.customer_id || 'guest',
        customer_email: customer?.email || '',
        item_count: order.line_items?.length || 0,
        kiosk_order: 'yes'
      },
      description: `Order #${order.id} - ${customerName}`,
      receipt_email: customer?.email,
      shipping: customer?.shipping ? {
        name: customerName,
        address: {
          line1: customer.shipping.address_1,
          line2: customer.shipping.address_2,
          city: customer.shipping.city,
          state: customer.shipping.state,
          postal_code: customer.shipping.postcode,
          country: customer.shipping.country
        },
        phone: customer.shipping.phone
      } : undefined
    };

    console.log('🔵 [DEBUG] Creating payment intent with payload:', JSON.stringify({
      endpoint: '/api/payments/create-payment-intent',
      ...paymentIntentPayload
    }, null, 2));

    const { clientSecret } = await post('/api/payments/create-payment-intent', paymentIntentPayload);
    console.log('🟢 [DEBUG] Payment intent created with client secret:', clientSecret ? '***' + clientSecret.slice(-8) : 'none');

    // 2. Prepare and log card payment confirmation
    const confirmPaymentPayload = {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: {
          name: customerName,
          email: customer?.email,
          phone: customer?.phone,
          address: {
            line1: customer?.billing?.address_1,
            line2: customer?.billing?.address_2,
            city: customer?.billing?.city,
            state: customer?.billing?.state,
            postal_code: customer?.billing?.postcode,
            country: customer?.billing?.country
          }
        },
      },
      receipt_email: customer?.email
    };

    console.log('🔵 [DEBUG] Confirming card payment with payload:', JSON.stringify({
      endpoint: 'stripe.confirmCardPayment',
      clientSecret: clientSecret ? '***' + clientSecret.slice(-8) : 'none',
      ...confirmPaymentPayload
    }, null, 2));

    const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(
      clientSecret,
      confirmPaymentPayload
    );

    if (paymentIntent) {
      console.log('🟢 [DEBUG] Payment intent confirmed:', {
        id: paymentIntent.id,
        amount: paymentIntent.amount,
        status: paymentIntent.status,
        charges: paymentIntent.charges?.data?.map(c => ({
          id: c.id,
          amount: c.amount,
          status: c.status,
          payment_method: c.payment_method_details?.type
        }))
      });
    }

    if (stripeError) {
      const errorStatus = {
        status: 'failed',
        note: `Payment failed: ${stripeError.message}`,
        error_code: stripeError.code,
        error_type: stripeError.type,
        payment_method: stripeError.payment_method?.type,
        decline_code: stripeError.decline_code
      };
      
      console.error('🔴 [ERROR] Payment failed with error:', JSON.stringify(errorStatus, null, 2));
      
      // Update order status to failed
      console.log('🔵 [DEBUG] Updating order status to failed:', JSON.stringify({
        orderId: order.id,
        ...errorStatus
      }, null, 2));
      
      await failCcOrder(order.id, { error_message: stripeError.message });
      throw new Error(stripeError.message);
    }

    // 3. Payment succeeded: create WC order (completed) + send receipt via backend
    if (paymentIntent.status === 'succeeded') {
      const wcOrder = await completeCcOrder(order.id, { payment_intent_id: paymentIntent.id });
      const result = {
        success: true,
        paymentIntent,
        orderId: wcOrder.id,
        order: wcOrder,
        kioskCcOrderId: order.id
      };
      onSuccess?.(result);
      return result;
    }

    const result = {
      success: true,
      paymentIntent,
      orderId: order.id
    };

    onSuccess?.(result);
    return result;
    
  } catch (error) {
    console.error('Payment processing error:', error);
    onError?.(error);
    throw error;
  }
};

/**
 * Process a swipe payment
 * @param {Object} params - Payment parameters
 * @param {string} params.paymentIntentId - The payment intent ID
 * @param {Object} params.paymentMethod - Payment method details
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const processSwipePayment = async ({ paymentIntentId, paymentMethod }, onSuccess, onError) => {
  try {
    if (!paymentIntentId) {
      throw new Error('No payment intent ID provided');
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      paymentIntentId,
      {
        payment_method: paymentMethod
      }
    );

    if (error) {
      throw error;
    }

    onSuccess?.({ paymentIntent });
    return { paymentIntent, error: null };
  } catch (error) {
    console.error('Swipe payment error:', error);
    onError?.(error);
    return { paymentIntent: null, error };
  }
};

/**
 * Process a swipe payment order creation
 * @param {Object} orderData - The order data to create the WooCommerce order
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 * @param {Function} getSession - Optional function to get current session
 */
export const processSwipeOrder = async (orderData, onSuccess, onError, getSession = null) => {
  try {
    if (!orderData?.cartItems || orderData.cartItems.length === 0) {
      throw new Error('No items in cart');
    }

    console.log('Processing SWIPE payment for order data:', orderData);
    
    // Check if we already have an order ID to prevent duplicates
    if (orderData.id) {
      console.log('Using existing order ID:', orderData.id);
      return { success: true, data: orderData };
    }

    // Calculate subtotal from cart items
    const subtotal = orderData.cartItems.reduce((sum, item) => sum + getLineChargeTotal(item), 0);

    // Calculate credit card fee if not already included
    const feePercentage = parseFloat(process.env.REACT_APP_CREDIT_CARD_FEE_PERCENTAGE || '2.5');
    const feeAmount = parseFloat((subtotal * (feePercentage / 100)).toFixed(2));
    const total = parseFloat((subtotal + feeAmount).toFixed(2));

    // Ensure order data includes the correct total with fees
    const orderWithFees = {
      ...orderData,
      total: orderData.total || total.toString(),
      fee_lines: [{
        name: process.env.REACT_APP_CREDIT_CARD_FEE_NAME || 'Credit Card Processing Fee',
        total: feeAmount.toString()
      }]
    };
    
    // Create the order with correct fees
    const order = await createWooCommerceOrder(orderWithFees);
    
    if (!order || !order.id) {
      throw new Error('Failed to create order');
    }
    
    console.log('Order created for swipe payment:', order.id);
    
    // Return the order with the correct total including fees
    const result = {
      success: true,
      data: {
        ...order,
        status: 'processing',
        payment_method: 'swipe',
        payment_status: 'completed',
        transaction_id: `swipe_${Date.now()}`,
        note: 'Swipe payment processed successfully'
      }
    };

    // Call success handler with the result
    onSuccess?.(result);
    
    // Create WC order (completed) + send receipt
    try {
      const updatedOrder = await completeCcOrder(order.id, {});
      console.log('Order completed successfully:', updatedOrder);
      return { ...result, order: updatedOrder };
    } catch (updateError) {
      console.error('Failed to complete order after swipe:', updateError);
      return result;
    }
  } catch (error) {
    console.error('Swipe payment error:', error);
    try {
      if (order?.id) {
        await failCcOrder(order.id, { error_message: error.message });
      }
    } catch (updateError) {
      console.error('Failed to mark order as failed:', updateError);
    }
    
    onError?.(error);
    throw error;
  }
};
